import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Stack,
  RadioGroup,
  Radio,
  FormLabel
} from '@mui/material'
import {
  PlayArrow,
  Download,
  Schedule,
  Refresh,
  Visibility,
  GetApp,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Assessment,
  Inventory,
  AttachMoney,
  ReceiptLong,
  DateRange as DateRangeIcon
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import PageHeader from '../components/ui/PageHeader'
import reportingService from '../services/reportingService'
import { categoryService } from '../services/productService'
import { warehouseService, Warehouse } from '../services/warehouseService'
import type {
  SalesReportRequest,
  InventoryReportRequest,
  PricingReportRequest,
  FinancialReportRequest,
  ReportJobStatus
} from '../services/reportingService'

interface CategoryOption {
  id: string
  name: string
}

const ReportGeneratorMUI: React.FC = () => {
  const [reportType, setReportType] = useState<'sales' | 'inventory' | 'pricing' | 'financial'>('sales')
  const [executionMode, setExecutionMode] = useState<'background' | 'instant'>('instant')
  const [startDate, setStartDate] = useState<Date | null>(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  const [format, setFormat] = useState<'json' | 'csv' | 'pdf'>('csv')
  const [loading, setLoading] = useState(false)
  const [refreshingJobs, setRefreshingJobs] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  // Report Jobs
  const [reportJobs, setReportJobs] = useState<ReportJobStatus[]>([])
  const [jobStatusDialog, setJobStatusDialog] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<ReportJobStatus | null>(null)

  // Dynamic Options
  const [categories, setCategories] = useState<CategoryOption[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])

  // Sales report specific fields
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day')
  const [customerId, setCustomerId] = useState('')
  const [customerType, setCustomerType] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [includeCancelled, setIncludeCancelled] = useState(false)

  // Inventory report specific fields
  const [warehouseId, setWarehouseId] = useState('')
  const [lowStockThreshold, setLowStockThreshold] = useState<number | ''>('')
  const [includeInactive, setIncludeInactive] = useState(false)

  // Pricing report specific fields
  const [tierId, setTierId] = useState('')
  const [productId, setProductId] = useState('')
  const [includeOverrides, setIncludeOverrides] = useState(true)

  // Financial report specific fields
  const [financialReportType, setFinancialReportType] = useState<'unpaid_invoices' | 'payment_reconciliation' | 'revenue_analysis'>('unpaid_invoices')
  const [paymentGateway, setPaymentGateway] = useState('')

  // Instant Report Result Preview
  const [instantResult, setInstantResult] = useState<any | null>(null)

  const loadReportJobs = useCallback(async () => {
    try {
      setRefreshingJobs(true)
      const jobs = await reportingService.getReportJobs(20)
      setReportJobs(jobs || [])
    } catch (err: any) {
      console.warn('Could not fetch report jobs from server:', err)
    } finally {
      setRefreshingJobs(false)
    }
  }, [])

  // Initial data load
  useEffect(() => {
    loadReportJobs()

    // Fetch categories for dropdown
    categoryService.listCategories({ per_page: 100 })
      .then((res: any) => {
        if (res?.items) {
          setCategories(res.items.map((c: any) => ({ id: c.id, name: c.name })))
        } else if (Array.isArray(res)) {
          setCategories(res.map((c: any) => ({ id: c.id, name: c.name })))
        }
      })
      .catch((err: any) => console.warn('Categories load error:', err))

    // Fetch warehouses for dropdown
    warehouseService.getWarehouses()
      .then((data: any) => {
        if (Array.isArray(data)) {
          setWarehouses(data)
        }
      })
      .catch((err: any) => console.warn('Warehouses load error:', err))
  }, [loadReportJobs])

  // Periodic polling for background jobs
  useEffect(() => {
    const hasActiveJobs = reportJobs.some(j => j.status === 'pending' || j.status === 'in_progress')
    if (!hasActiveJobs) return

    const interval = setInterval(() => {
      loadReportJobs()
    }, 4000)

    return () => clearInterval(interval)
  }, [reportJobs, loadReportJobs])

  const setPresetRange = (preset: 'today' | '7days' | '30days' | 'month' | 'quarter' | 'year') => {
    const now = new Date()
    setEndDate(now)
    switch (preset) {
      case 'today':
        setStartDate(now)
        break
      case '7days':
        setStartDate(new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000))
        break
      case '30days':
        setStartDate(new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000))
        break
      case 'month': {
        const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
        setStartDate(firstDay)
        break
      }
      case 'quarter': {
        const quarterMonth = Math.floor(now.getMonth() / 3) * 3
        const firstDayQ = new Date(now.getFullYear(), quarterMonth, 1)
        setStartDate(firstDayQ)
        break
      }
      case 'year': {
        const firstDayY = new Date(now.getFullYear(), 0, 1)
        setStartDate(firstDayY)
        break
      }
    }
  }

  const generateReport = async () => {
    if (!startDate || !endDate) {
      setErrorMsg('Please select both start and end dates.')
      return
    }

    setErrorMsg(null)
    setSuccessMsg(null)
    setLoading(true)

    try {
      const dateRange = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      }

      const isBackground = executionMode === 'background'
      let result: any

      switch (reportType) {
        case 'sales': {
          const salesRequest: SalesReportRequest = {
            ...dateRange,
            group_by: groupBy,
            customer_id: customerId || undefined,
            customer_type: customerType || undefined,
            category_id: categoryId || undefined,
            include_cancelled: includeCancelled,
            format
          }
          result = await reportingService.generateSalesReport(salesRequest, isBackground)
          break
        }
        case 'inventory': {
          const inventoryRequest: InventoryReportRequest = {
            ...dateRange,
            warehouse_id: warehouseId || undefined,
            category_id: categoryId || undefined,
            low_stock_threshold: lowStockThreshold !== '' ? Number(lowStockThreshold) : undefined,
            include_inactive: includeInactive,
            format
          }
          result = await reportingService.generateInventoryReport(inventoryRequest, isBackground)
          break
        }
        case 'pricing': {
          const pricingRequest: PricingReportRequest = {
            ...dateRange,
            tier_id: tierId || undefined,
            product_id: productId || undefined,
            include_overrides: includeOverrides,
            format
          }
          result = await reportingService.generatePricingReport(pricingRequest, isBackground)
          break
        }
        case 'financial': {
          const financialRequest: FinancialReportRequest = {
            ...dateRange,
            report_type: financialReportType,
            payment_gateway: paymentGateway || undefined,
            customer_id: customerId || undefined,
            format
          }
          result = await reportingService.generateFinancialReport(financialRequest, isBackground)
          break
        }
      }

      if (result && 'job_id' in result) {
        setSuccessMsg(`Background job #${result.job_id.slice(0, 8)} started! Progress will be tracked below.`)
        await loadReportJobs()
      } else {
        setInstantResult(result)
        setSuccessMsg('Report generated successfully! See live preview and download options below.')
      }
    } catch (error: any) {
      console.error('Error generating report:', error)
      setErrorMsg(error?.response?.data?.detail || error?.message || 'Error generating report. Please check filters.')
    } finally {
      setLoading(false)
    }
  }

  const exportInstantResultAsJson = () => {
    if (!instantResult) return
    const blob = new Blob([JSON.stringify(instantResult, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${reportType}_report_${Date.now()}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const viewJobStatus = async (jobId: string) => {
    try {
      const status = await reportingService.getReportJobStatus(jobId)
      setJobStatus(status)
      setJobStatusDialog(jobId)
    } catch (error) {
      console.error('Error fetching job status:', error)
    }
  }

  const downloadReport = async (jobId: string) => {
    try {
      const blob = await reportingService.downloadReport(jobId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `report_${jobId.slice(0, 8)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error downloading report:', error)
      setErrorMsg('Error downloading report file. It may still be generating.')
    }
  }

  const getStatusChip = (status: string) => {
    switch (status) {
      case 'completed':
        return <Chip icon={<CheckCircle />} label="Completed" size="small" color="success" />
      case 'in_progress':
        return <Chip icon={<CircularProgress size={12} color="inherit" />} label="In Progress" size="small" color="primary" />
      case 'pending':
        return <Chip icon={<HourglassEmpty />} label="Queued" size="small" color="warning" />
      case 'failed':
        return <Chip icon={<ErrorIcon />} label="Failed" size="small" color="error" />
      default:
        return <Chip label={status} size="small" />
    }
  }

  const renderReportTypeFields = () => {
    switch (reportType) {
      case 'sales':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Group By Timeframe</InputLabel>
                <Select
                  value={groupBy}
                  label="Group By Timeframe"
                  onChange={(e) => setGroupBy(e.target.value as any)}
                >
                  <MenuItem value="day">Daily</MenuItem>
                  <MenuItem value="week">Weekly</MenuItem>
                  <MenuItem value="month">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Category Filter</InputLabel>
                <Select
                  value={categoryId}
                  label="Category Filter"
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Customer Type</InputLabel>
                <Select
                  value={customerType}
                  label="Customer Type"
                  onChange={(e) => setCustomerType(e.target.value)}
                >
                  <MenuItem value="">All Customer Types</MenuItem>
                  <MenuItem value="retail">Retail Buyer</MenuItem>
                  <MenuItem value="reseller">Reseller / Boutique</MenuItem>
                  <MenuItem value="wholesaler">Wholesaler / Distributor</MenuItem>
                  <MenuItem value="broker">Agent / Broker</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Specific Customer UUID (Optional)"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCancelled}
                    onChange={(e) => setIncludeCancelled(e.target.checked)}
                  />
                }
                label="Include Cancelled / Voided Orders"
              />
            </Grid>
          </>
        )

      case 'inventory':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Warehouse</InputLabel>
                <Select
                  value={warehouseId}
                  label="Warehouse"
                  onChange={(e) => setWarehouseId(e.target.value)}
                >
                  <MenuItem value="">All Warehouses</MenuItem>
                  {warehouses.map((wh) => (
                    <MenuItem key={wh.id} value={wh.id}>
                      {wh.name} {wh.code ? `(${wh.code})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryId}
                  label="Category"
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Low Stock Alert Threshold"
                type="number"
                placeholder="e.g. 10"
                value={lowStockThreshold}
                onChange={(e) => setLowStockThreshold(e.target.value ? parseInt(e.target.value) : '')}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  />
                }
                label="Include Inactive / Discontinued Products"
              />
            </Grid>
          </>
        )

      case 'pricing':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Pricing Tier ID (Optional)"
                value={tierId}
                onChange={(e) => setTierId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Product ID / SKU (Optional)"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeOverrides}
                    onChange={(e) => setIncludeOverrides(e.target.checked)}
                  />
                }
                label="Include Customer Specific Price Overrides"
              />
            </Grid>
          </>
        )

      case 'financial':
        return (
          <>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Financial Ledger Report</InputLabel>
                <Select
                  value={financialReportType}
                  label="Financial Ledger Report"
                  onChange={(e) => setFinancialReportType(e.target.value as any)}
                >
                  <MenuItem value="unpaid_invoices">Unpaid Invoices & Overdue Balances</MenuItem>
                  <MenuItem value="payment_reconciliation">Payment & Gateway Reconciliation</MenuItem>
                  <MenuItem value="revenue_analysis">Revenue & Tax Analysis</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Payment Gateway Filter (Optional)"
                placeholder="e.g. Razorpay, Bank Transfer"
                value={paymentGateway}
                onChange={(e) => setPaymentGateway(e.target.value)}
              />
            </Grid>
          </>
        )

      default:
        return null
    }
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <PageHeader
          title="Enterprise Report Generator"
          subtitle="Generate, preview, schedule, and download analytical reports across Sales, Inventory, Pricing, and Finance"
        />

        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMsg(null)}>
            {errorMsg}
          </Alert>
        )}

        {successMsg && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg(null)}>
            {successMsg}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Main Report Configuration */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {reportType === 'sales' && <Assessment color="primary" />}
                    {reportType === 'inventory' && <Inventory color="primary" />}
                    {reportType === 'pricing' && <AttachMoney color="primary" />}
                    {reportType === 'financial' && <ReceiptLong color="primary" />}
                    <Typography variant="h6" fontWeight={700}>
                      Configure Report Parameters
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={2.5}>
                  {/* Report Type Selector */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Report Module</InputLabel>
                      <Select
                        value={reportType}
                        label="Report Module"
                        onChange={(e) => {
                          setReportType(e.target.value as any)
                          setInstantResult(null)
                        }}
                      >
                        <MenuItem value="sales">📊 Sales & Revenue Performance</MenuItem>
                        <MenuItem value="inventory">📦 Inventory & Stock Valuation</MenuItem>
                        <MenuItem value="pricing">🏷️ Pricing Tiers & Margins</MenuItem>
                        <MenuItem value="financial">💳 Financial Ledger & Invoices</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Export Format */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Output Format</InputLabel>
                      <Select
                        value={format}
                        label="Output Format"
                        onChange={(e) => setFormat(e.target.value as any)}
                      >
                        <MenuItem value="csv">CSV Spreadsheet (.csv)</MenuItem>
                        <MenuItem value="json">JSON Data Stream (.json)</MenuItem>
                        <MenuItem value="pdf">PDF Document (.pdf)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Quick Preset Ranges */}
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <DateRangeIcon fontSize="small" /> Quick Date Presets:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label="Today" size="small" variant="outlined" onClick={() => setPresetRange('today')} clickable />
                      <Chip label="Last 7 Days" size="small" variant="outlined" onClick={() => setPresetRange('7days')} clickable />
                      <Chip label="Last 30 Days" size="small" variant="outlined" color="primary" onClick={() => setPresetRange('30days')} clickable />
                      <Chip label="This Month" size="small" variant="outlined" onClick={() => setPresetRange('month')} clickable />
                      <Chip label="This Quarter" size="small" variant="outlined" onClick={() => setPresetRange('quarter')} clickable />
                      <Chip label="Year to Date" size="small" variant="outlined" onClick={() => setPresetRange('year')} clickable />
                    </Stack>
                  </Grid>

                  {/* Start / End Date */}
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={setStartDate}
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={setEndDate}
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </Grid>

                  {/* Specific Fields */}
                  {renderReportTypeFields()}

                  {/* Execution Mode */}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Execution Mode</FormLabel>
                      <RadioGroup
                        row
                        value={executionMode}
                        onChange={(e) => setExecutionMode(e.target.value as any)}
                      >
                        <FormControlLabel
                          value="instant"
                          control={<Radio size="small" />}
                          label="⚡ Instant On-Screen Preview & Download"
                        />
                        <FormControlLabel
                          value="background"
                          control={<Radio size="small" />}
                          label="⏳ Background Task (Async Worker Job)"
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>

                  {/* Action Button */}
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
                      onClick={generateReport}
                      disabled={loading}
                      fullWidth
                      sx={{ py: 1.5, fontWeight: 700, textTransform: 'none', fontSize: '1rem' }}
                    >
                      {loading
                        ? 'Processing Report...'
                        : executionMode === 'background'
                        ? 'Enqueue Background Report Job'
                        : 'Generate Live Report Preview'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Live Instant Preview Card */}
            {instantResult && (
              <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Live Report Preview
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Generated at {instantResult.generated_at ? new Date(instantResult.generated_at).toLocaleString() : new Date().toLocaleString()}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Download />}
                        onClick={exportInstantResultAsJson}
                      >
                        Export JSON
                      </Button>
                    </Stack>
                  </Box>

                  {/* Summary Metric Cards */}
                  {instantResult.summary && (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      {Object.entries(instantResult.summary).map(([key, val]: [string, any]) => {
                        const formattedKey = key.replace(/_/g, ' ').toUpperCase()
                        let displayVal = val
                        if (typeof val === 'number') {
                          if (key.includes('revenue') || key.includes('amount') || key.includes('value')) {
                            displayVal = `₹${val.toLocaleString('en-IN')}`
                          } else {
                            displayVal = val.toLocaleString()
                          }
                        }
                        return (
                          <Grid item xs={6} sm={3} key={key}>
                            <Paper
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                textAlign: 'center',
                                borderRadius: 2,
                                backgroundColor: 'background.default'
                              }}
                            >
                              <Typography variant="caption" color="text.secondary" fontWeight={600} noWrap>
                                {formattedKey}
                              </Typography>
                              <Typography variant="h6" fontWeight={700} color="primary.main">
                                {displayVal}
                              </Typography>
                            </Paper>
                          </Grid>
                        )
                      })}
                    </Grid>
                  )}

                  {/* Preview Table */}
                  {Array.isArray(instantResult.data) && instantResult.data.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 380, borderRadius: 2 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {Object.keys(instantResult.data[0])
                              .filter(k => typeof instantResult.data[0][k] !== 'object')
                              .map((col) => (
                                <TableCell key={col} sx={{ fontWeight: 700, backgroundColor: 'grey.100' }}>
                                  {col.replace(/_/g, ' ').toUpperCase()}
                                </TableCell>
                              ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {instantResult.data.slice(0, 50).map((row: any, idx: number) => (
                            <TableRow key={idx} hover>
                              {Object.keys(instantResult.data[0])
                                .filter(k => typeof instantResult.data[0][k] !== 'object')
                                .map((col) => {
                                  let cellVal = row[col]
                                  if (typeof cellVal === 'number' && (col.includes('price') || col.includes('revenue') || col.includes('amount') || col.includes('value'))) {
                                    cellVal = `₹${cellVal.toLocaleString('en-IN')}`
                                  }
                                  return (
                                    <TableCell key={col}>
                                      {cellVal !== null && cellVal !== undefined ? String(cellVal) : '-'}
                                    </TableCell>
                                  )
                                })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">No records matched the selected filters for this time period.</Alert>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>

          {/* Background Jobs Sidebar */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
              <CardContent sx={{ p: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Schedule color="primary" />
                    <Typography variant="h6" fontWeight={700}>
                      Background Jobs
                    </Typography>
                  </Box>
                  <Tooltip title="Refresh Job List">
                    <span>
                      <IconButton size="small" onClick={loadReportJobs} disabled={refreshingJobs}>
                        <Refresh fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>

                {reportJobs.length === 0 ? (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No background report jobs found. Trigger one in background mode to queue async export.
                  </Alert>
                ) : (
                  <List disablePadding>
                    {reportJobs.map((job) => (
                      <ListItem
                        key={job.job_id}
                        divider
                        sx={{
                          px: 1,
                          py: 1.5,
                          '&:hover': { backgroundColor: 'action.hover' },
                          borderRadius: 1
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ component: 'div' }}
                          secondaryTypographyProps={{ component: 'div' }}
                          primary={
                            <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                              <Typography variant="body2" fontWeight={700}>
                                Job #{job.job_id?.slice(0, 8) ?? job.job_id}
                              </Typography>
                              {getStatusChip(job.status)}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="caption" color="text.secondary" display="block">
                                Created: {new Date(job.created_at).toLocaleString()}
                              </Typography>
                              {job.status === 'in_progress' && (
                                <LinearProgress
                                  variant="determinate"
                                  value={job.progress_percentage || 20}
                                  sx={{ mt: 1, borderRadius: 1 }}
                                />
                              )}
                              {job.error_message && (
                                <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                                  {job.error_message}
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                        <Box display="flex" alignItems="center" ml={1}>
                          <Tooltip title="View Details">
                            <IconButton size="small" onClick={() => viewJobStatus(job.job_id)}>
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {job.status === 'completed' && (
                            <Tooltip title="Download CSV Report">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => downloadReport(job.job_id)}
                              >
                                <GetApp fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Job Status Dialog */}
        <Dialog
          open={jobStatusDialog !== null}
          onClose={() => setJobStatusDialog(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Report Job Details
          </DialogTitle>
          <DialogContent dividers>
            {jobStatus && (
              <Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">JOB ID</Typography>
                    <Typography variant="body1" fontWeight={600}>{jobStatus.job_id}</Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">STATUS</Typography>
                    <Box mt={0.5}>{getStatusChip(jobStatus.status)}</Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">PROGRESS</Typography>
                    <Typography variant="body2" fontWeight={600}>{jobStatus.progress_percentage}%</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={jobStatus.progress_percentage}
                      sx={{ mt: 0.5, borderRadius: 1 }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">CREATED AT</Typography>
                    <Typography variant="body2">{new Date(jobStatus.created_at).toLocaleString()}</Typography>
                  </Grid>

                  {jobStatus.started_at && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">STARTED AT</Typography>
                      <Typography variant="body2">{new Date(jobStatus.started_at).toLocaleString()}</Typography>
                    </Grid>
                  )}

                  {jobStatus.completed_at && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">COMPLETED AT</Typography>
                      <Typography variant="body2">{new Date(jobStatus.completed_at).toLocaleString()}</Typography>
                    </Grid>
                  )}

                  {jobStatus.error_message && (
                    <Grid item xs={12}>
                      <Alert severity="error">{jobStatus.error_message}</Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setJobStatusDialog(null)}>Close</Button>
            {jobStatus?.status === 'completed' && (
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => downloadReport(jobStatus.job_id)}
              >
                Download Export File
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  )
}

export default ReportGeneratorMUI